import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, BookOpen, ShieldCheck, Dumbbell, Files,
  Cloud, CloudOff, ExternalLink, Link as LinkIcon, Play, Video as VideoIcon,
  Youtube,
} from "lucide-react";
import { isStaff, isAdminLike, CATEGORIES as CAT_KEYS, CATEGORY_LABEL } from "@/lib/roles";
import { detectVideoEmbed, isVideoMime } from "@/lib/videoEmbed";

const CATEGORIES = {
  regulation: { label: "Reglamento", icon: ShieldCheck, drive: "Reglamento" },
  playbook:   { label: "Playbook",   icon: BookOpen,    drive: "Playbook" },
  training:   { label: "Entrenamiento", icon: Dumbbell, drive: "Entrenamientos" },
  other:      { label: "Otros",      icon: Files,       drive: "Otros" },
};

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [driveStatus, setDriveStatus] = useState({ connected: false, can_connect: false });
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null); // doc currently being reproduced
  const canEdit = isStaff(user?.role);

  // A doc is playable if: it has a link_url that we recognize OR it's a video file in Drive
  const isPlayable = (d) => {
    if (d.link_url) return Boolean(detectVideoEmbed(d.link_url) || /^https?:/i.test(d.link_url));
    return isVideoMime(d.content_type);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [docsRes, statusRes] = await Promise.all([
        api.get("/documents"),
        api.get("/drive/status"),
      ]);
      setDocs(docsRes.data);
      setDriveStatus(statusRes.data);
      // If returning from OAuth callback, surface a friendly toast
      if (new URLSearchParams(window.location.search).get("drive_connected") === "1") {
        toast.success("Google Drive conectado correctamente");
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (err) {
      // status may fail if drive not connected; documents still empty
      console.error(err); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  const connectDrive = async () => {
    try {
      const { data } = await api.get("/drive/connect");
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar la conexión");
    }
  };

  const disconnectDrive = async () => {
    if (!confirm("¿Desconectar Google Drive? Los archivos seguirán en Drive, pero la app dejará de mostrarlos hasta que vuelvas a conectar.")) return;
    try {
      await api.post("/drive/disconnect");
      toast.success("Google Drive desconectado");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: doc.content_type });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al descargar");
    }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar documento? Se borrará también de Google Drive.")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Documento eliminado");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const filtered = filter === "all" ? docs : docs.filter(d => d.category === filter);

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="overline flex items-center gap-2">
              Knowledge Base
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] ${driveStatus.connected ? "border-[#34C759] text-[#34C759]" : "border-[#FF9F0A] text-[#FF9F0A]"}`} data-testid="drive-status-chip">
                {driveStatus.connected ? <><Cloud size={9}/> Google Drive</> : <><CloudOff size={9}/> Drive desconectado</>}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none">DOCUMENTOS</h1>
            <p className="text-[#A1A1AA] mt-2 text-sm">Reglamento, playbook, planes de entrenamiento — alojados en el Google Drive del club.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <button onClick={() => setShowForm(true)} className="btn-primary text-xs flex items-center gap-2" data-testid="upload-doc-btn">
                <Upload size={14}/> Subir documento
              </button>
            )}
            {driveStatus.connected && driveStatus.can_connect && (
              <button onClick={disconnectDrive} className="btn-ghost text-xs flex items-center gap-2 border border-[#27272A] text-[#A1A1AA]" data-testid="drive-disconnect-btn">
                <LinkIcon size={12}/> Desconectar Drive
              </button>
            )}
          </div>
        </div>

        {!driveStatus.connected && (
          <ConnectDriveBanner
            canConnect={driveStatus.can_connect}
            onConnect={connectDrive}
          />
        )}

        {showForm && (
          <UploadForm
            onClose={() => setShowForm(false)}
            onSaved={() => { setShowForm(false); loadAll(); }}
            user={user}
            driveConnected={driveStatus.connected}
          />
        )}

        <div className="flex gap-1 mb-6 flex-wrap">
          <Pill active={filter==="all"} onClick={() => setFilter("all")} label="Todos"/>
          {Object.entries(CATEGORIES).map(([k, v]) => (
            <Pill key={k} active={filter===k} onClick={() => setFilter(k)} label={v.label}/>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && (
            <div className="md:col-span-3 tactical-card p-10 text-center text-[#71717A]">Cargando…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="md:col-span-3 tactical-card p-10 text-center">
              <FileText size={32} className="mx-auto text-[#71717A] mb-3"/>
              <div className="text-[#A1A1AA]">
                {driveStatus.connected
                  ? "No hay documentos en esta sección todavía."
                  : "Conecta Google Drive (banner arriba) para ver archivos, o agrega un link externo en Entrenamientos."}
              </div>
            </div>
          )}
          {!loading && filtered.map(d => {
            const Cat = CATEGORIES[d.category] || CATEGORIES.other;
            const linkInfo = d.link_url ? detectVideoEmbed(d.link_url) : null;
            const playable = isPlayable(d);
            // Use Youtube/Video icon when the doc is a video link
            const Icon = linkInfo?.kind === "youtube" ? Youtube : (d.link_url || isVideoMime(d.content_type) ? VideoIcon : Cat.icon);
            const iconColor = linkInfo?.kind === "youtube" ? "#FF0000" : (d.link_url ? "#A78BFA" : "#007AFF");
            return (
              <div key={d.id} className="tactical-card p-5 flex flex-col" data-testid={`doc-card-${d.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <Icon size={28} style={{ color: iconColor }}/>
                  <span className="overline flex items-center gap-1">
                    {Cat.label}
                    {d.link_url && <span className="text-[9px] text-[#A78BFA] border border-[#A78BFA] px-1 py-0.5 ml-1">LINK</span>}
                  </span>
                </div>
                <div className="font-display text-xl text-white">{(d.title || d.original_filename || "").toUpperCase()}</div>
                {d.description && <div className="text-sm text-[#A1A1AA] mt-1 flex-1">{d.description}</div>}
                <div className="text-xs text-[#71717A] mt-3 font-mono truncate" title={d.original_filename}>{d.original_filename}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(d.categories || []).length === 0 ? (
                    <span className="text-[9px] uppercase tracking-widest text-[#A78BFA] border border-[#A78BFA] px-1.5 py-0.5">Todo el club</span>
                  ) : (
                    (d.categories || []).map(c => (
                      <span key={c} className="text-[9px] uppercase tracking-widest text-[#A1A1AA] border border-[#27272A] px-1.5 py-0.5">{CATEGORY_LABEL[c] || c}</span>
                    ))
                  )}
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-[#27272A]">
                  {playable && (
                    <button
                      onClick={() => setPlaying(d)}
                      className="btn-primary text-xs flex-1 flex items-center justify-center gap-1.5"
                      data-testid={`play-doc-${d.id}`}
                    >
                      <Play size={12}/> REPRODUCIR
                    </button>
                  )}
                  {!d.link_url && !playable && (
                    <button onClick={() => download(d)} className="btn-primary text-xs flex-1" data-testid={`download-doc-${d.id}`}>DESCARGAR</button>
                  )}
                  {!d.link_url && playable && (
                    <button
                      onClick={() => download(d)}
                      className="btn-ghost text-xs border border-[#27272A] text-[#A1A1AA] hover:text-white px-2"
                      data-testid={`download-doc-${d.id}`}
                      title="Descargar el archivo"
                    >
                      <ExternalLink size={12} className="rotate-90"/>
                    </button>
                  )}
                  {d.web_view_link && (
                    <a
                      href={d.web_view_link}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-ghost text-xs border border-[#27272A] text-[#A1A1AA] hover:text-white px-2 inline-flex items-center"
                      data-testid={`open-link-${d.id}`}
                      title={d.link_url ? "Abrir en pestaña nueva" : "Abrir en Google Drive"}
                    >
                      <ExternalLink size={12}/>
                    </a>
                  )}
                  {canEdit && <button onClick={() => remove(d.id)} className="btn-ghost text-xs text-[#FF3B30]" data-testid={`del-doc-${d.id}`}><Trash2 size={14}/></button>}
                </div>
              </div>
            );
          })}
        </div>
        {playing && <VideoPlayerModal doc={playing} onClose={() => setPlaying(null)}/>}
        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

// =================== VIDEO PLAYER MODAL ===================
function VideoPlayerModal({ doc, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const linkInfo = doc.link_url ? detectVideoEmbed(doc.link_url) : null;
  const isFileVideo = !doc.link_url && isVideoMime(doc.content_type);

  // For Drive video files we stream via our authenticated endpoint and turn
  // the response into a blob URL the <video> tag can consume.
  useEffect(() => {
    if (!isFileVideo) return;
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await api.get(`/documents/${doc.id}/stream`, { responseType: "blob" });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(new Blob([res.data], { type: doc.content_type || "video/mp4" }));
        setBlobUrl(createdUrl);
      } catch (err) {
        toast.error("No se pudo cargar el video");
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [doc.id, doc.content_type, isFileVideo]);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="player-modal">
      <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Reproducción</div>
            <div className="font-display text-xl sm:text-2xl text-white">{(doc.title || "").toUpperCase()}</div>
          </div>
          <button onClick={onClose} className="btn-ghost text-xs" data-testid="player-close">✕ CERRAR</button>
        </div>
        <div className="bg-black border border-[#27272A] aspect-video w-full">
          {linkInfo && (linkInfo.kind === "youtube" || linkInfo.kind === "vimeo" || linkInfo.kind === "drive") && (
            <iframe
              src={linkInfo.embedUrl}
              title={doc.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              data-testid="player-iframe"
            />
          )}
          {linkInfo && linkInfo.kind === "video" && (
            <video src={linkInfo.embedUrl} controls autoPlay className="w-full h-full" data-testid="player-video"/>
          )}
          {!linkInfo && doc.link_url && (
            // Unknown link type → open externally
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center px-6">
              <ExternalLink size={32} className="text-[#A78BFA]"/>
              <div className="text-white font-display text-lg">FORMATO NO INCRUSTABLE</div>
              <div className="text-sm text-[#A1A1AA]">No podemos reproducir este link aquí. Ábrelo en una pestaña nueva.</div>
              <a href={doc.link_url} target="_blank" rel="noreferrer" className="btn-primary text-xs flex items-center gap-2">
                <ExternalLink size={12}/> ABRIR EN PESTAÑA NUEVA
              </a>
            </div>
          )}
          {isFileVideo && (
            blobUrl ? (
              <video src={blobUrl} controls autoPlay className="w-full h-full" data-testid="player-video-blob"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#71717A] text-sm">Cargando video...</div>
            )
          )}
        </div>
        {doc.description && <div className="text-sm text-[#A1A1AA] mt-3">{doc.description}</div>}
      </div>
    </div>
  );
}

function ConnectDriveBanner({ canConnect, onConnect }) {
  return (
    <div
      className="tactical-card p-5 mb-6 border-l-4 border-[#FF9F0A]"
      style={{ background: "linear-gradient(120deg, rgba(255,159,10,0.08) 0%, transparent 60%)" }}
      data-testid="connect-drive-banner"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <CloudOff className="text-[#FF9F0A] shrink-0" size={28}/>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl text-white">CONECTA GOOGLE DRIVE</div>
          <div className="text-sm text-[#A1A1AA] mt-0.5">
            {canConnect
              ? "Los documentos del club viven en el Drive de ferroviarios.puentealtofa@gmail.com. Conecta una sola vez y todos los usuarios podrán subir y descargar archivos desde la app."
              : "Aún no se ha conectado el Google Drive del club. Pídele a un Super Admin o Admin Directivo que lo haga desde esta pantalla."}
          </div>
        </div>
        {canConnect && (
          <button onClick={onConnect} className="btn-primary text-xs flex items-center gap-2" data-testid="drive-connect-btn">
            <Cloud size={14}/> CONECTAR DRIVE
          </button>
        )}
      </div>
    </div>
  );
}

const Pill = ({ active, onClick, label }) => (
  <button onClick={onClick} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border ${active ? "border-[#007AFF] text-white bg-[#1A1A1A]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}>
    {label}
  </button>
);

function UploadForm({ onClose, onSaved, user, driveConnected = true }) {
  const isAdmin = isAdminLike(user?.role);
  const coachCats = user?.coach_categories || [];
  const availableCats = isAdmin ? CAT_KEYS : (coachCats.length ? coachCats : CAT_KEYS);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("playbook");
  const [description, setDescription] = useState("");
  const [cats, setCats] = useState([]);
  const [file, setFile] = useState(null);
  // source = "file" → upload a real file to Drive
  //        = "link" → store an external URL (YouTube/Vimeo/mp4) — only valid for training
  const [source, setSource] = useState("file");
  const [linkUrl, setLinkUrl] = useState("");
  const [saving, setSaving] = useState(false);

  // Link mode is only valid for Entrenamientos. Auto-flip back to file if user changes the type.
  useEffect(() => {
    if (source === "link" && category !== "training") setSource("file");
  }, [category, source]);

  const toggleCat = (c) => setCats(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const linkPreview = source === "link" ? detectVideoEmbed(linkUrl) : null;

  // Drive folders allow ONE category per file → guide user to pick a single one
  const driveTargetLabel = () => {
    const top = CATEGORIES[category]?.drive || "Otros";
    if (cats.length === 0) return `Ferroviarios APP / ${top}`;
    const cat = CATEGORY_LABEL[cats[0]] || cats[0];
    return `Ferroviarios APP / ${top} / ${cat}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (source === "file" && !file) return toast.error("Selecciona un archivo");
    if (source === "link" && !linkUrl.trim()) return toast.error("Pega un link (YouTube, Vimeo, etc.)");
    if (source === "file" && !driveConnected) return toast.error("Para subir archivos primero conecta Google Drive");
    if (!isAdmin && cats.length === 0) return toast.error("Como Coach debes seleccionar al menos una categoría");
    if (source === "file" && cats.length > 1) {
      const ok = confirm(
        "Drive solo guarda el archivo en UNA carpeta. Subiremos el documento a " +
        (CATEGORY_LABEL[cats[0]] || cats[0]) +
        " y los miembros del resto de categorías seleccionadas también podrán verlo. ¿Continuar?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const qs = new URLSearchParams({
        title, category, description,
        categories: cats.join(","),
      });
      if (source === "link") {
        qs.set("link_url", linkUrl.trim());
        await api.post(`/documents?${qs.toString()}`);
        toast.success("Link guardado");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/documents?${qs.toString()}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Documento subido a Drive");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="tactical-card p-6 mb-6 space-y-4" data-testid="upload-form">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-2xl text-white">NUEVO DOCUMENTO</div>
        {source === "file" && (
          <div className="text-[10px] text-[#34C759] font-mono uppercase tracking-widest flex items-center gap-1" data-testid="drive-target-hint">
            <Cloud size={11}/> {driveTargetLabel()}
          </div>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="label-tactical">Título</label>
          <input required className="input-tactical" value={title} onChange={e => setTitle(e.target.value)} data-testid="doc-title-input"/>
        </div>
        <div>
          <label className="label-tactical">Tipo</label>
          <select className="input-tactical" value={category} onChange={e => setCategory(e.target.value)} data-testid="doc-type-select">
            <option value="regulation">Reglamento</option>
            <option value="playbook">Playbook</option>
            <option value="training">Entrenamientos</option>
            <option value="other">Otros</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="label-tactical">Descripción</label>
          <input className="input-tactical" value={description} onChange={e => setDescription(e.target.value)} data-testid="doc-desc-input"/>
        </div>
        <div className="md:col-span-2">
          <label className="label-tactical">Rama / equipo {!isAdmin && <span className="text-[#FF9F0A]">(obligatorio)</span>}</label>
          <div className="flex flex-wrap gap-2">
            {availableCats.map(c => {
              const active = cats.includes(c);
              return (
                <button
                  type="button"
                  key={c}
                  onClick={() => toggleCat(c)}
                  data-testid={`doc-cat-${c}`}
                  className={`px-3 py-2 border text-xs uppercase tracking-wider font-bold transition-colors ${active ? "border-[#007AFF] bg-[#1A1A1A] text-white" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                >
                  {CATEGORY_LABEL[c]}
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-[#71717A] mt-1.5">
            {cats.length === 0
              ? (isAdmin ? "Sin selección → club-wide (queda al nivel del tipo, sin subcarpeta de equipo)." : "Como Coach, debes seleccionar al menos una de tus categorías.")
              : "Drive guardará el archivo en la primera categoría; las demás también podrán verlo en la app."}
          </div>
        </div>

        {/* Source toggle: only show for Entrenamientos (link mode = solo training) */}
        {category === "training" && (
          <div className="md:col-span-2">
            <label className="label-tactical">¿Qué quieres subir?</label>
            <div className="flex border border-[#27272A]" data-testid="source-toggle">
              <button
                type="button"
                onClick={() => setSource("file")}
                className={`flex-1 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5 ${source === "file" ? "bg-[#1A1A1A] text-white border-b-2 border-b-[#007AFF]" : "text-[#A1A1AA] hover:text-white"}`}
                data-testid="source-file"
              >
                <Upload size={11}/> Archivo (Drive)
              </button>
              <button
                type="button"
                onClick={() => setSource("link")}
                className={`flex-1 py-2 text-xs uppercase tracking-wider font-bold border-l border-[#27272A] flex items-center justify-center gap-1.5 ${source === "link" ? "bg-[#1A1A1A] text-white border-b-2 border-b-[#A78BFA]" : "text-[#A1A1AA] hover:text-white"}`}
                data-testid="source-link"
              >
                <LinkIcon size={11}/> Link (YouTube, etc.)
              </button>
            </div>
          </div>
        )}

        {source === "file" && (
          <div className="md:col-span-2">
            <label className="label-tactical">Archivo (PDF, imagen, video MP4, etc.)</label>
            <input required={source === "file"} type="file" className="input-tactical" onChange={e => setFile(e.target.files[0])} data-testid="doc-file-input"/>
            {!driveConnected && (
              <div className="text-[10px] text-[#FF9F0A] mt-1.5">Para subir archivos primero conecta Google Drive (banner arriba). Los links sí funcionan sin Drive.</div>
            )}
          </div>
        )}

        {source === "link" && (
          <div className="md:col-span-2 space-y-2">
            <label className="label-tactical">URL del video</label>
            <input
              required={source === "link"}
              type="url"
              className="input-tactical"
              placeholder="https://www.youtube.com/watch?v=... | https://vimeo.com/... | https://...mp4"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              data-testid="doc-link-input"
            />
            {linkPreview && (
              <div className="text-[10px] text-[#34C759] uppercase tracking-widest flex items-center gap-1" data-testid="link-preview">
                <Play size={10}/> Detectado: {linkPreview.kind}
                {linkPreview.id && <span className="text-[#71717A]"> · ID {linkPreview.id}</span>}
              </div>
            )}
            {linkUrl && !linkPreview && (
              <div className="text-[10px] text-[#FF9F0A] uppercase tracking-widest">
                ⚠ No se reconoce un video incrustable. Se abrirá en pestaña nueva al reproducir.
              </div>
            )}
            <div className="text-[10px] text-[#71717A]">
              Para Entrenamientos puedes pegar un link de YouTube, Vimeo, un archivo .mp4 directo o un link de Drive. Se reproducirá dentro de la app sin descargar.
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="btn-ghost text-xs">CANCELAR</button>
        <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="doc-submit-btn">
          {saving ? "GUARDANDO..." : (source === "link" ? "GUARDAR LINK" : "SUBIR A DRIVE")}
        </button>
      </div>
    </form>
  );
}
